import { useEffect, useRef, useState } from "react";
import { Play, Pause, Download } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const fmt = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");

export const AudioPlayer = ({ src, title, subtitle, devSample, downloadHref, testid = "audio-player" }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const on = (e, f) => a.addEventListener(e, f);
    on("timeupdate", () => setTime(a.currentTime));
    on("loadedmetadata", () => setDur(a.duration));
    on("ended", () => setPlaying(false));
    on("error", () => setFailed(true));
  }, [src]);

  const toggle = () => {
    const a = ref.current;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => setFailed(true));
    }
  };

  return (
    <div className="card-white p-5 sm:p-6" data-testid={testid}>
      <audio ref={ref} src={src} preload="metadata" />
      {devSample && (
        <p className="mb-3 rounded-full bg-[var(--gold-soft)] px-3 py-1 text-xs font-semibold text-[var(--gold-deep)]" data-testid={`${testid}-dev-label`}>
          Development sample tone — not a real generated song
        </p>
      )}
      <div className="flex items-center gap-4">
        <button onClick={toggle} disabled={failed} data-testid={`${testid}-play-button`}
          aria-label={playing ? `Pause ${title}` : `Play ${title}`}
          className="relative grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[var(--plum)] text-[var(--gold)] transition-transform hover:scale-105 disabled:opacity-40">
          {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-display truncate text-lg">{title}</p>
          {subtitle && <p className="truncate text-sm text-[var(--plum-muted)]">{subtitle}</p>}
          <div className="mt-2 flex items-center gap-3">
            <input type="range" min={0} max={dur || 0} step={0.1} value={time} aria-label="Seek"
              onChange={(e) => { ref.current.currentTime = Number(e.target.value); setTime(Number(e.target.value)); }}
              data-testid={`${testid}-progress-slider`} className="h-1 w-full cursor-pointer accent-[var(--gold)]" />
            <span className="w-20 shrink-0 text-right text-xs tabular-nums text-[var(--plum-muted)]" aria-live="off">{fmt(time)} / {fmt(dur)}</span>
          </div>
        </div>
        {downloadHref && (
          <a href={downloadHref} className="btn-ghost !p-3" aria-label="Download song" data-testid={`${testid}-download`}>
            <Download className="h-4 w-4" />
          </a>
        )}
      </div>
      {failed && <p className="mt-3 text-sm text-[var(--rose)]" role="alert">This audio couldn't be loaded. Please try again later or contact support.</p>}
      {playing && (
        <div className="mt-4 flex h-6 items-end gap-1" aria-hidden>
          {Array.from({ length: 24 }).map((_, i) => (
            <span key={i} className="eq-bar w-1 rounded-full bg-[var(--rose)]/70" style={{ height: "100%", animationDelay: `${(i % 6) * 0.12}s` }} />
          ))}
        </div>
      )}
    </div>
  );
};

export const LyricsDisplay = ({ text, language, testid = "lyrics-display" }) => (
  <div lang={language?.code} dir={language?.rtl ? "rtl" : "ltr"} className="lyrics-text text-base text-[var(--plum-text)] sm:text-lg" data-testid={testid}>
    {text}
  </div>
);

export const ExplanationNote = ({ token, version, language, initial, endpoint }) => {
  const [exp, setExp] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setExp(initial || null); }, [initial, version]);
  if (!language || language.code?.startsWith("en")) return null;
  const load = async () => {
    setBusy(true); setErr("");
    try { setExp((await api.post(endpoint || `/orders/${token}/lyrics/explain`, { version })).data.explanation); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  return (
    <aside className="rounded-2xl border border-dashed border-[var(--plum-muted)]/40 bg-[var(--ivory)] p-5" data-testid="lyrics-explanation" lang="en" dir="ltr">
      <p className="eyebrow !text-[var(--plum-muted)]">English explanation · not sung</p>
      {exp ? (
        <>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[var(--plum-2)]" data-testid="lyrics-explanation-text">{exp.text}</p>
          <p className="mt-3 text-xs text-[var(--plum-muted)]">An AI-written summary to help you understand the {language.english_name} lyrics above. It isn't a word-for-word translation, and only the lyrics above will be sung.</p>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-[var(--plum-2)]">Want to know what the {language.english_name} lyrics say? Get a short English explanation — the song itself stays in {language.english_name}.</p>
          <button className="btn-ghost mt-3 !px-4 !py-2 text-sm" onClick={load} disabled={busy} data-testid="lyrics-explain-button">{busy ? "Preparing…" : "Show English explanation"}</button>
        </>
      )}
      {err && <p className="mt-2 text-sm text-[var(--rose)]" role="alert">{err}</p>}
    </aside>
  );
};
